import { useState, useEffect, useRef } from 'react';
import {
  differenceInMilliseconds,
  startOfSecond,
  startOfMinute,
  startOfHour,
  startOfDay,
  addSeconds,
  addMinutes,
  addHours,
  addDays
} from "date-fns";

export type useRunOnceProps = {
  fn: () => any;
  sessionKey?: string;
};

const getSessionStorageOrDefault = (key: any, defaultValue: any) => {
  const stored = sessionStorage.getItem(key);
  if (!stored) {
    return defaultValue;
  }
  return JSON.parse(stored);
}

const useSessionStorage = (key: any, defaultValue: any) => {
  const [value, setValue] = useState(
    getSessionStorageOrDefault(key, defaultValue)
  );

  useEffect(() => {
    sessionStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
}

const getLocalStorageOrDefault = (key: any, defaultValue: any) => {
  const stored = localStorage.getItem(key);
  if (!stored) {
    return defaultValue;
  }
  return JSON.parse(stored);
}

const useLocalStorage = (key: any, defaultValue: any) => {
  const [value, setValue] = useState(
    getLocalStorageOrDefault(key, defaultValue)
  );

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
}

// second | minute | hour | day

const thresholdMap: any = {
  second: {
    start: startOfSecond,
    add: addSeconds
  },
  minute: {
    start: startOfMinute,
    add: addMinutes
  },
  hour: {
    start: startOfHour,
    add: addHours
  },
  day: {
    start: startOfDay,
    add: addDays
  }
};

const msUntilNext = (threshold: any) => {
  const { start, add } = thresholdMap[threshold];
  const date = new Date();
  return differenceInMilliseconds(
    add(start(date), 1),
    date
  );
}

const startOfThreshold = (threshold: any) => {
  if (!threshold) {
    return new Date();
  } else {
    return thresholdMap[threshold].start(new Date());
  }
}

const useDateTime = (threshold: any) => {
  const [date, setDate] = useState(
    startOfThreshold(threshold)
  );
  const timer: any = useRef(0);

  useEffect(() => {
    if (threshold) {
      const delayedTimeChange = () => {
        timer.current = setTimeout(() => {
          delayedTimeChange();
        }, msUntilNext(threshold));

        setDate(startOfThreshold(threshold));
      }

      delayedTimeChange();
      return () => clearTimeout(timer.current);
    }
  }, [threshold]);

  return date;
}

// use to run any callbacks once on mount or once per session if you pass in the sessionKey here
const useRunOnce = ({ fn, sessionKey } : useRunOnceProps) => {
  const triggered = useRef<boolean>(false);

  useEffect(() => {
    const hasBeenTriggered = sessionKey
      ? sessionStorage.getItem(sessionKey)
      : triggered.current;

    if (!hasBeenTriggered) {
      fn();
      triggered.current = true;

      if (sessionKey) {
        sessionStorage.setItem(sessionKey, "true");
      }
    }
  }, [fn, sessionKey]);

  return null;
};

export { useSessionStorage, useLocalStorage, useDateTime, useRunOnce };